import PropTypes from 'prop-types'

/**
 * Steps Component
 *
 * A reusable step indicator component that displays a horizontal sequence of steps
 * with numbers and labels. Supports clicking on steps to navigate between them.
 *
 * @param {Object} props - Component props
 * @param {string[]} props.steps - Array of step labels to display
 * @param {number} props.currentStep - Index of the currently active step (0-based)
 * @param {function} props.onStepClick - Callback function called when a step is clicked
 * @param {string} [props.className] - Optional additional CSS class name
 * @param {boolean} [props.clickable=true] - Whether steps can be clicked to navigate
 * @param {boolean} [props.numbered=true] - Whether steps should be numbered
 *
 * @example
 * // Basic usage
 * <Steps
 *   steps={['Step 1', 'Step 2', 'Step 3']}
 *   currentStep={1}
 *   onStepClick={(index) => setCurrentStep(index)}
 *   numbered={false}
 * />
 *
 * @example
 * // With custom styling and non-clickable
 * <Steps
 *   steps={['Details', 'Payment', 'Confirmation']}
 *   currentStep={0}
 *   onStepClick={() => {}}
 *   className="custom-steps"
 *   clickable={false}
 *   numbered={false}
 * />
 */

const normalizeStep = (step) => (typeof step === 'string' ? { label: step, indicator: null } : step)

const FormSteps = ({
  steps,
  currentStep,
  onStepClick,
  className = '',
  clickable = true,
  numbered = true,
}) => {
  const handleStepClick = (index) => {
    if (clickable && onStepClick) {
      onStepClick(index)
    }
  }

  const stepClassName = (index) => {
    let classes = 'step'
    if (index === currentStep) {
      classes += ' step--active'
    }
    if (clickable) {
      classes += ' step--clickable'
    }
    return classes
  }

  return (
    <div className={`form-steps ${className}`.trim()}>
      {steps.map((step, index) => {
        const { label, indicator } = normalizeStep(step)
        return (
          <div
            className={stepClassName(index)}
            key={`step-${index}-${label}`}
            onClick={() => handleStepClick(index)}
            role={clickable ? 'button' : 'presentation'}
            tabIndex={clickable ? 0 : -1}
            onKeyDown={(e) => {
              if (clickable && (e.key === 'Enter' || e.key === ' ')) {
                e.preventDefault()
                handleStepClick(index)
              }
            }}
          >
            {numbered && <div className="step__number">{index + 1}</div>}
            <span className="step__label">{label}</span>
            {indicator}
          </div>
        )
      })}
    </div>
  )
}

FormSteps.propTypes = {
  steps: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        label: PropTypes.string.isRequired,
        indicator: PropTypes.node,
      }),
    ]),
  ).isRequired,
  currentStep: PropTypes.number.isRequired,
  onStepClick: PropTypes.func.isRequired,
  className: PropTypes.string,
  clickable: PropTypes.bool,
  numbered: PropTypes.bool,
}

export default FormSteps
